import { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Logo } from "../components/Logo";
import LogoHint from "../components/LogoHint";
import ModeWashShades from "../components/ModeWashShades";
import { useMode } from "../context/ModeContext";
import { ThemeToggle } from "../components/ThemeToggle";
import { NumberTicker } from "../components/motion/NumberTicker";
import NotificationsBell from "../components/NotificationsBell";
import ForYouChip from "../components/ForYouChip";
import BrainBellBadge from "../components/BrainBellBadge";
import { OnboardingTour } from "../components/WaveTwoFeatures";
import axios from "axios";
import {
  LayoutDashboard,
  Package,
  Sparkles,
  Calendar,
  CheckSquare,
  CreditCard,
  Settings,
  UserCircle,
  LogOut,
  Menu,
  X,
  Shield,
  Globe,
  Share2,
  DollarSign,
  Video,
  Gift,
  Layers,
  Coins,
  Film,
  Activity,
  Mail,
  TrendingUp,
  Tag,
  Megaphone,
  Newspaper,
  Users,
  Image as ImageIcon,
  Palette,
  Film as FilmIcon,
  Clapperboard,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardLayout = ({ isAdmin = false }) => {
  const { user, logout, getDisplayTier, token } = useAuth();
  const { mode: activeMode, config: modeConfig } = (() => {
    try { return useMode(); }
    catch (_) { return { mode: "marketing", config: { accent: "#FF4F27", label: "Marketing" } }; }
  })();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(0);

  const displayTier = getDisplayTier();

  useEffect(() => {
    const fetchTokenBalance = async () => {
      if (!token) return;
      try {
        const r = await axios.get(`${API_URL}/api/tokens/balance`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTokenBalance(r.data.tokens || 0);
      } catch (e) {
        console.error("Error fetching token balance:", e);
      }
    };
    fetchTokenBalance();
    const interval = setInterval(fetchTokenBalance, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  // Essentials always visible across every studio
  const sharedRail = [
    { path: "/dashboard",           icon: LayoutDashboard, label: "Dashboard" },
    { path: "/dashboard/community", icon: Users,           label: "Community", badge: "NEW" },
    { path: "/dashboard/profile",   icon: UserCircle,      label: "My Profile" },
    { path: "/dashboard/tokens",    icon: Coins,           label: "Tokens" },
    { path: "/dashboard/settings",  icon: Settings,        label: "Settings" },
  ];

  // Per-studio sidebars (Marketing | Designer | CGI). The "Plan" entry stays
  // at the END of each mode's rail so it always renders right before
  // Community (which lives in the shared rail). That gives every studio its
  // own subscription panel sitting between its last mode-item and Community.
  const modeRails = {
    designer: [
      { path: "/dashboard/designer",     icon: Palette,    label: "Studio" },
      { path: "/dashboard/articles",     icon: Newspaper,  label: "Articles", badge: "NEW" },
      { path: "/dashboard/ai-video",     icon: Video,      label: "AI Video" },
      { path: "/dashboard/photo-reel",   icon: Film,       label: "Photo Reel" },
      { path: "/dashboard/bulk",         icon: Layers,     label: "Bulk" },
      { path: "/dashboard/gallery",      icon: Film,       label: "My Works" },
      { path: "/dashboard/tasks",        icon: Activity,   label: "Tasks" },
      { path: "/dashboard/subscription", icon: CreditCard, label: "Plan", testid: "nav-plan-designer" },
    ],
    cgi: [
      { path: "/dashboard/movie",        icon: Clapperboard, label: "Movie", badge: "NEW" },
      { path: "/dashboard/cgi",          icon: FilmIcon,   label: "FX Lab", badge: "BETA" },
      { path: "/dashboard/explainer",    icon: Megaphone,  label: "Explainer", badge: "NEW" },
      { path: "/dashboard/ai-video",     icon: Video,      label: "AI Video" },
      { path: "/dashboard/photo-reel",   icon: Film,       label: "Photo Reel" },
      { path: "/dashboard/bulk",         icon: Layers,     label: "Bulk" },
      { path: "/dashboard/timeline",     icon: Layers,     label: "Timeline" },
      { path: "/dashboard/composer",     icon: Sparkles,   label: "Composer" },
      { path: "/dashboard/gallery",      icon: Film,       label: "Renders" },
      { path: "/dashboard/tasks",        icon: Activity,   label: "Render Queue" },
      { path: "/dashboard/subscription", icon: CreditCard, label: "Plan", testid: "nav-plan-cgi" },
    ],
  };

  const userNavItems = [
    sharedRail[0],
    ...(modeRails[activeMode] || modeRails.designer),
    ...sharedRail.slice(1),
  ];

  const adminNavItems = [
    { path: "/admin",               icon: Shield,      label: "Admin" },
    { path: "/admin/live",          icon: Activity,    label: "Live" },
    { path: "/admin/payout",        icon: DollarSign,  label: "Payouts" },
    { path: "/admin/waitlist",      icon: Mail,        label: "Waitlist" },
    { path: "/admin/system-health", icon: Activity,    label: "Health" },
    { path: "/admin/offers",        icon: Gift,        label: "Offers" },
    { path: "/admin/coupons",       icon: Tag,         label: "Coupons" },
    { path: "/admin/moodboard",     icon: ImageIcon,   label: "Moodboard" },
  ];

  const navItems = isAdmin ? adminNavItems : userNavItems;

  const isActive = (path) => {
    if (path === "/dashboard" || path === "/admin") return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  return (
    <div
      className="min-h-screen text-ink-950 relative transition-colors duration-500"
      style={{ background: "#FAFAFA" }}
    >
      {/* Animated mode-wash shades — drift in the active studio's accent */}
      <ModeWashShades />

      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-ink-950/40 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Floating glass sidebar */}
      <aside
        className={`fixed z-50 inset-y-0 left-0 transform transition-transform duration-300 ease-out
          ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0 lg:top-6 lg:bottom-6 lg:left-6 lg:h-[calc(100vh-3rem)]
          w-72 lg:w-64`}
        data-testid="dashboard-sidebar"
      >
        <div className="flex h-full flex-col rounded-3xl border border-ink-200/70 bg-white/95 shadow-ambient overflow-hidden">
          {/* Logo */}
          <div className="p-6">
            <LogoHint className="mb-1.5 pl-1" />
            <Logo size={32} tagline animated />
          </div>

          {/* User chip */}
          <div className="mx-4 rounded-2xl bg-ink-50 border border-ink-200/70 p-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-ink-950 text-white flex items-center justify-center text-xs font-bold tracking-tight">
                {user?.name?.charAt(0)?.toUpperCase() || "U"}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-ink-950 truncate">{user?.name}</p>
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink-500">
                  {displayTier} plan
                </p>
              </div>
            </div>

            <Link
              to="/dashboard/tokens"
              data-testid="token-balance-link"
              className="mt-3 group flex items-center justify-between px-3 py-2 rounded-xl bg-white border border-ink-200/80 hover:border-ink-950 transition-colors"
            >
              <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-ink-500">
                <Coins className="w-4 h-4 text-coral-500" />
                Tokens
              </span>
              <span
                className="text-sm font-black font-display text-ink-950"
                data-testid="sidebar-token-balance"
              >
                {displayTier === "ultimate" ? "∞" : (
                  <NumberTicker value={tokenBalance} duration={0.8} />
                )}
              </span>
            </Link>
          </div>

          {/* Nav */}
          <nav className="flex-1 p-3 mt-3 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={item.testid || `nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-semibold transition-all duration-200
                    ${active
                      ? "bg-ink-950 text-white shadow-[0_8px_24px_rgba(10,10,10,0.18)]"
                      : "text-ink-500 hover:bg-ink-100 hover:text-ink-950"}`}
                >
                  <item.icon className="w-4 h-4" />
                  <span className="flex-1">{item.label}</span>
                  {item.badge && (
                    <Badge variant="accent" className="!py-0 !px-2 !text-[9px]">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })}

            {/* Cross-link between user / admin dashboards */}
            {!isAdmin && user?.is_admin && (
              <Link
                to="/admin"
                onClick={() => setSidebarOpen(false)}
                className="mt-3 flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-semibold border border-ink-200 text-ink-950 hover:bg-ink-100"
              >
                <Shield className="w-4 h-4 text-coral-500" />
                Admin Panel
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/dashboard"
                onClick={() => setSidebarOpen(false)}
                className="mt-3 flex items-center gap-3 px-4 py-2.5 rounded-2xl text-sm font-semibold border border-ink-200 text-ink-950 hover:bg-ink-100"
              >
                <LayoutDashboard className="w-4 h-4" />
                Back to Dashboard
              </Link>
            )}
          </nav>

          {/* Footer */}
          <div className="p-4 border-t border-ink-200/70 space-y-3">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="flex-1 justify-start"
              >
                <LogOut className="w-4 h-4" />
                Logout
              </Button>
              <ThemeToggle />
            </div>
            <p className="text-[10px] text-center text-ink-500 leading-relaxed">
              Working together for a safe future
            </p>
          </div>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 bg-ink-50/95 border-b border-ink-200">
        <div className="flex items-center justify-between px-4 py-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </Button>
          <div className="flex flex-col items-center">
            <LogoHint className="mb-0.5" />
            <Logo size={28} tagline={false} animated />
          </div>
          <div className="flex items-center gap-1">
            <NotificationsBell />
            <ThemeToggle />
          </div>
        </div>
      </header>

      {/* Desktop floating notifications + theme toggle (top-right) */}
      <div className="hidden lg:flex fixed top-4 right-6 z-30 items-center gap-2">
        {!isAdmin && <ForYouChip />}
        {!isAdmin && <BrainBellBadge />}
        <NotificationsBell testIdSuffix="-desktop" />
      </div>

      {/* Page content */}
      <main className="relative z-10 lg:pl-80 pl-0 pr-0 lg:pr-6 pt-6 lg:pt-8 pb-12 px-4 lg:px-0">
        <div className="max-w-7xl mx-auto">
          <Outlet />
        </div>
      </main>

      {/* First-time onboarding tour — auto-shows once per user via localStorage.
          Suppressed on admin-layout routes so the modal doesn't block admin
          dialog clicks (iter-46/47 UX note). */}
      {!isAdmin && <OnboardingTour />}
    </div>
  );
};

export default DashboardLayout;
